// =============================================================================
// DEV caller simulation panel on /call — BaseQL operations (shared layer: imported by BOTH bundles)
// =============================================================================
// 2 operations, both JSON strings of
// { available: boolean, number: string | null, envFallback: boolean, environment: 'development' | 'staging' | 'production' | null }
// for the CURRENT user:
//   - devCallerSimulationStatus    (query)    - does the panel exist here, and which number is active
//   - devCallerSimulationSetNumber (mutation) - set an E.164 number, or '' to clear
// envFallback = the runtime .env number (ZW_DEV_CALLER_SIMULATION*) applies to this user while the panel is empty,
// so the panel never says "normal browser call" when it is not. It never carries the .env number itself, and is
// always false on staging (panel only there).
// environment = where the panel is shown: the personal development runtime, or staging with
// ZW_CALLER_SIMULATION_STAGING=true (see server/dev-caller-simulation.ts); lets the panel warn on staging.
//
// The engine discards all browser call metadata, so the number cannot travel with the call. It is kept
// server-side in memory per user id (server/dev-caller-simulation.ts) BEFORE the call and read once when the
// browser session starts. Every environment guard of that module still applies to the hook itself.
//
// Access, fail closed — one rule, canUseCallerSimulation() in server/auth.ts: on STAGING every signed-in,
// non-disabled account (ZwickRöll's testers included, 16/09/2026), on the personal runtime administrators only:
//   - anonymous/disabled: the global authenticated-operation middleware (server/auth.ts) throws
//   - mutation: in CALLER_SIMULATION_MUTATIONS, so it carries that rule instead of the blanket administrator
//     middleware; the resolver checks again (defence in depth) and is the only place that refuses on the
//     environment itself (DEV_CALLER_SIMULATION_UNAVAILABLE)
//   - query: authenticated only by the global middleware, so the resolver returns available=false to everyone the
//     rule excludes and outside the two allowed environments
//   - an account that still reaches us but may no longer use it HERE loses a value it set earlier (here, and before
//     every browser call in server/call-access.ts): in practice an administrator demoted on the personal runtime. A
//     staging tester whose role changes keeps the number, because the staging rule never looked at the role. A
//     disabled/archived account is refused by the authenticated middleware before this file runs, so its number is
//     neither dropped nor usable - no call of that account can start - until the account comes back.
//
// SERVER-IMPORT STYLE as in crm-lookup-resolvers.ts: `/server/` modules are empty on the client and are used only
// inside resolve(). Error messages and log payloads never contain the number.
// =============================================================================

import { logger } from '@adornis/base/logging.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getContext } from '@adornis/baseql/server/context.js'; // emptied on client by build shim
import { type AuthenticationContext, canUseCallerSimulation } from '../server/auth.js'; // emptied on client
import {
  devCallerSimulationEnvironment,
  envSimulatedCallerNumberActive,
  forgetUiSimulatedCallerNumber,
  uiSimulatedCallerNumber,
  updateUiSimulatedCallerNumber,
} from '../server/dev-caller-simulation.js'; // emptied on client

const UNAVAILABLE_STATUS = JSON.stringify({ available: false, number: null, envFallback: false, environment: null });

/** The logged-in user id allowed to use the panel here; undefined for everyone else, including the server context. */
function simulationUserID(): string | undefined {
  const context = getContext() as AuthenticationContext | undefined;
  if (!context || context.serverContext) return undefined;
  const userID = typeof context.userID === 'string' && context.userID ? context.userID : undefined;
  if (!userID) return undefined;
  if (canUseCallerSimulation(context)) return userID;
  // No longer allowed in THIS environment after setting a number - in practice an administrator demoted on the
  // personal runtime: drop it, so this account's next browser call is a normal one. A disabled account is refused by
  // the authenticated middleware before this runs; dropping it here too is defence in depth, not the live path.
  forgetUiSimulatedCallerNumber(userID);
  return undefined;
}

function statusFor(userID: string): string {
  const environment = devCallerSimulationEnvironment();
  if (!environment) return UNAVAILABLE_STATUS;
  const number = uiSimulatedCallerNumber(userID) ?? null;
  const envFallback = !number && envSimulatedCallerNumberActive(userID);
  return JSON.stringify({ available: true, number, envFallback, environment });
}

export const devCallerSimulationStatus = registerQuery({
  type: () => String,
  operationName: 'devCallerSimulationStatus',
  resolve: () => async () => {
    const userID = simulationUserID();
    if (!userID) return UNAVAILABLE_STATUS;
    return statusFor(userID);
  },
  params: [],
});

export const devCallerSimulationSetNumber = registerMutation({
  type: () => String,
  operationName: 'devCallerSimulationSetNumber',
  resolve: (callerNumber: string) => async () => {
    const userID = simulationUserID();
    if (!userID) throw new Error('DEV_CALLER_SIMULATION_NOT_ALLOWED');
    const update = updateUiSimulatedCallerNumber(userID, callerNumber);
    if (update === 'unavailable') {
      throw new Error('DEV_CALLER_SIMULATION_UNAVAILABLE: not enabled in this environment');
    }
    if (update === 'invalid')
      throw new Error('INVALID_CALLER_NUMBER: E.164 expected (+, country code, number, no spaces)');
    logger.warn(
      {
        isDevSimulatedCaller: true,
        hasNumber: update === 'set',
        isStagingDeployment: devCallerSimulationEnvironment() === 'staging',
      },
      'DEV caller simulation: panel number changed for the next browser call',
    );
    return statusFor(userID);
  },
  params: [{ name: 'callerNumber', type: () => String }],
});
